import {
  AtividadeToiPorAtividadeOutput,
  MxlAtividadeToiOutput,
  MxlEconomiaPorCategoriasRow,
  ToiJsonDados,
} from '../models/toi-api.model';
import {
  ToiDetalhe,
  ToiEconomiaCategoriaRow,
  ToiModeloCodigo,
  ToiSimNao,
  ToiStatusEvent,
  ToiTipoModelo,
} from '../models/toi-detalhe.model';
import {
  mapChecklistToi,
  parseJsonDadosToi,
  parseJsonToi,
  parseNumeroToi,
  resolveDataConclusao,
  resolverDadosPorAtividade,
} from './toi-api.util';
import {
  aplicarLegendasDoChecklistToi,
  normalizarMidiasToi,
  resolverMidiasToi,
} from './toi-midia.util';
import {
  buildStatusToiLabel,
  normalizeToiStatusLabel,
  resolveStatusToiFromEventos,
} from './toi-status.util';

/** Texto fixo do bloco "ATENÇÃO" exibido no detalhe do TOI. */
export const TOI_ATENCAO_PARAGRAFOS: readonly string[] = [
  'A COPASA tomará as providências para eliminação da(s) infração(ões) identificada(s);',
  'As infrações sujeitam o usuário à: advertência, pagamento de multa (sanção pecuniária), ressarcimento dos prejuízos impostos ao prestador, à interrupção da prestação do abastecimento de água;',
  'É facultado ao usuário o direito de apresentar defesa formal no prazo de até 15 (quinze) dias corridos da data de recebimento deste Termo. Ao acatar este termo e optar pelo pagamento da multa e ressarcimentos devidos sem apresentação de recurso, o usuário fará jus ao desconto de 25% sobre o valor da multa;',
  'Em caso de reincidência devidamente comprovada no período de 04 anos poderá ser cobrado em dobro os valores relativos à multa;',
  'A tabela de sanções aplicáveis encontra-se disponível no site: www.copasa.com.br;',
  'Para mais esclarecimentos, dirija-se à agência de atendimento da COPASA MG mais próxima ou ligue 115.',
  'Este assunto está regulamentado por meio da Resolução Arsae-MG nº 149/21.',
];

export function buildToiEntregueUsuario(flagEntregueMaos: boolean): ToiSimNao {
  return flagEntregueMaos ? 'SIM' : 'NÃO';
}

/** Resolve o tipo de modelo a partir de `tpToi`, com fallback em `cdModeloToi`. */
export function resolveTpToi(tpToi?: string | null, cdModeloToi?: string | null): ToiTipoModelo {
  const tp = tpToi?.trim();
  if (tp === 'MODELO_1' || tp === 'MODELO_2') {
    return tp;
  }

  const cd = cdModeloToi?.trim();
  if (cd === 'TOI_2A' || cd === 'TOI_2E') {
    return 'MODELO_2';
  }

  return 'MODELO_1';
}

export function buildTituloTermo(tpToi: ToiTipoModelo): string {
  return tpToi === 'MODELO_1'
    ? 'Termo de Ocorrência de Infração - Regularização pela Copasa'
    : 'Termo de Ocorrência de Infração - Regularização pelo Usuário';
}

export function buildModeloLabel(modelo: ToiModeloCodigo): string {
  const map: Record<ToiModeloCodigo, string> = {
    TOI_1A: 'TOI 1A',
    TOI_1E: 'TOI 1E',
    TOI_2A: 'TOI 2A',
    TOI_2E: 'TOI 2E',
  };
  return map[modelo];
}

/** Converte o retorno de /PorAtividade no modelo de detalhe consumido pela UI. */
export function mapAtividadeToiToDetalhe(response: AtividadeToiPorAtividadeOutput): ToiDetalhe {
  const dados = resolverDadosPorAtividade(response) as MxlAtividadeToiOutput;
  const dadosHstRecord = response.dadosHst;
  const tpToi = resolveTpToi(dados.tpToi, dados.cdModeloToi);
  const cdModeloToi = (dados.cdModeloToi as ToiModeloCodigo) || 'TOI_1A';
  const jsonDados = parseJsonDadosToi(dados.jsonDadosToi);
  const jsonFotos = parseJsonToi(dados.jsonControleFotos);
  const checklistToi = mapChecklistToi(response.checklistToi);

  const flags = {
    flagEntregueMaos: dados.flagEntregueMaos,
    flagRecusaRecebimento: dados.flagRecusaRecebimento,
    flagGerado: dados.flagGerado,
    flagExigeEnvioCorreios: dados.flagExigeEnvioCorreios,
    flagEnderecoPendenteTratamento: dados.flagEnderecoPendenteTratamento,
  };

  const eventosToi = response.eventosToi ?? [];
  const statusEvents = buildStatusEvents(eventosToi, dados, buildStatusToiLabel(flags));

  const ultimoStatusBruto =
    dados.dsStatusToi?.trim() || resolveStatusToiFromEventos(eventosToi, flags);
  const ultimoStatus = normalizeToiStatusLabel(ultimoStatusBruto) ?? ultimoStatusBruto;

  return {
    idAtividadeToi: dados.idAtividadeToi,
    idAtividade: dados.idAtividade,
    idAtividadeToiStatus: dados.idAtividadeToiStatus,
    idChecklist: dados.idChecklist ?? null,
    idFormulario: dados.idFormulario ?? null,
    numeroDocumento: dados.nrDocumento,
    cdModeloToi: dados.cdModeloToi,
    tpToi,
    flagGerado: dados.flagGerado,
    flagEntregueMaos: dados.flagEntregueMaos,
    flagRecusaRecebimento: dados.flagRecusaRecebimento,
    flagPermiteEdicao: dados.flagPermiteEdicao,
    flagSincronizadoMobile: dados.flagSincronizadoMobile,
    flagExigeEnvioCorreios: dados.flagExigeEnvioCorreios,
    flagEnderecoPendenteTratamento: dados.flagEnderecoPendenteTratamento,
    dataGeracao: dados.dataGeracao ?? '', // previne discrepância de nomes
    dataVistoria: dados.dataVistoria,
    dataConclusao: resolveDataConclusao(
      dados as unknown as Record<string, unknown>,
      dadosHstRecord
    ),
    dataEntregaMaos: dados.dataEntregaMaos ?? null,
    dataValidacao: dados.dataValidacao ?? null,
    cdUsuarioValidacao: dados.cdUsuarioValidacao ?? null,
    dataAprovacao: dados.dataAprovacao ?? null,
    cdUsuarioAprovacao: dados.cdUsuarioAprovacao ?? null,
    dataEnvioCorreios: dados.dataEnvioCorreios ?? null,
    dataPostagem: dados.dataPostagem ?? null,
    dataEntregaCorreios: dados.dataEntregaCorreios ?? null,
    dataUltimaOcorrencia: dados.dataUltimaOcorrencia ?? null,
    ultimaAlteracao: dados.dataUltimaAlteracao,
    usuarioAlteracao: dados.cdUsuarioUltimaAlteracao,
    cdResponsavelEquipe: dados.cdResponsavelEquipe ?? null,
    nmResponsavelEquipe: dados.nmResponsavelEquipe ?? null,
    gerenciaRegional: dados.dsGerenciaRegional,
    localidade: dados.dsLocalidade,
    enderecoUnidadeUsuaria: dados.dsEnderecoUnidade,
    matriculaImovel: dados.nrMatriculaImovel,
    usuarioCadastrado: dados.nmUsuarioCadastrado,
    identificadorUsuario: dados.nrIdentificadorUsuario,
    numeroOS: dados.numeroBa,
    cdServico: dados.cdServico,
    dsTipoInfracao: dados.dsTipoInfracao,
    dsTipoIrregularidade: dados.dsTipoIrregularidade,
    jsonDadosToi: dados.jsonDadosToi,
    jsonControleFotos: dados.jsonControleFotos,
    nrLoteCorreios: dados.nrLoteCorreios ?? null,
    nrAr: dados.nrAr ?? null,
    cdRastreio: dados.cdRastreio ?? null,
    dsRetornoCorreios: dados.dsRetornoCorreios ?? null,
    dsObservacaoTratamento: dados.dsObservacaoTratamento ?? null,
    dataCadastro: dados.dataCadastro,
    usuarioCadastro: dados.usuarioCadastro,
    dataAtualizacao: dados.dataAtualizacao ?? null,
    usuarioAtualizacao: dados.usuarioAtualizacao ?? null,

    checklistToi,
    irregularidadeMarcar: response.irregularidadeMarcar ?? [],

    modeloToi: tpToi,
    tituloTermo: buildTituloTermo(tpToi),
    modeloLabel: buildModeloLabel(cdModeloToi),
    toiEntregueUsuario: buildToiEntregueUsuario(dados.flagEntregueMaos),
    dsStatusToi: ultimoStatus,
    eventosToi: statusEvents,
    statusEvents,
    economiaPorCategorias: resolveEconomiaPorCategoriasToi(dados.economiaPorCategorias, jsonDados),
    irregularidades: jsonDados?.irregularidades ?? [],
    midias: normalizarMidiasToi(
      aplicarLegendasDoChecklistToi(resolverMidiasToi(jsonFotos), checklistToi)
    ),
    localizacaoPadrao: resolveLocalizacaoPadraoToi(
      dados as unknown as Record<string, unknown>,
      jsonDados
    ),
    atencaoParagrafos: [...TOI_ATENCAO_PARAGRAFOS],
  };
}

/** Histórico do TOI; sem eventos, monta um registro único com a última alteração. */
function buildStatusEvents(
  eventosToi: NonNullable<AtividadeToiPorAtividadeOutput['eventosToi']>,
  dados: MxlAtividadeToiOutput,
  statusLabel: string
): ToiStatusEvent[] {
  if (!eventosToi.length) {
    return [
      {
        idAtividadeToiHistoricoStatus: 0,
        usuarioAlteracao: dados.cdUsuarioUltimaAlteracao,
        dataInicio: dados.dataUltimaAlteracao,
        dataFim: null,
        status: statusLabel,
        observacao: null,
      },
    ];
  }

  return eventosToi.map((evento) => ({
    idAtividadeToiHistoricoStatus: evento.idAtividadeToiHistoricoStatus,
    usuarioAlteracao: evento.usuarioAlteracao ?? '',
    dataInicio: evento.dataInicio ?? '',
    dataFim: evento.dataFim ?? null,
    status: evento.status ?? '',
    observacao: null,
  }));
}

/** Economias vindas do MXL têm prioridade; o `jsonDadosToi` é o fallback do mobile. */
export function resolveEconomiaPorCategoriasToi(
  rows: MxlEconomiaPorCategoriasRow[] | undefined,
  jsonDados: ToiJsonDados | null
): ToiEconomiaCategoriaRow[] {
  const fromMxl = mapEconomiaPorCategoriasFromMxl(rows);
  if (fromMxl.length > 0) {
    return fromMxl;
  }
  return jsonDados?.economiaPorCategorias ?? [];
}

function mapEconomiaPorCategoriasFromMxl(
  rows: MxlEconomiaPorCategoriasRow[] | undefined
): ToiEconomiaCategoriaRow[] {
  if (!rows?.length) {
    return [];
  }

  return rows.map((row) => ({
    tipoLinha: row.tps,
    comercial: parseNumeroToi(row.comercial),
    industrial: parseNumeroToi(row.industrial),
    publica: parseNumeroToi(row.publica),
    residencial: parseNumeroToi(row.residencial),
    social: parseNumeroToi(row.social),
  }));
}

/** Localização do cadastro (API) ou a gravada no `jsonDadosToi` pelo mobile. */
export function resolveLocalizacaoPadraoToi(
  dados: Record<string, unknown>,
  jsonDados: ToiJsonDados | null
): string | null {
  const candidatos = [
    dados['localizacaoPadrao'],
    dados['LocalizacaoPadrao'],
    jsonDados?.localizacao,
    jsonDados?.['LOCALIZACAO'],
  ];

  for (const raw of candidatos) {
    if (raw == null) {
      continue;
    }
    const texto = String(raw).trim();
    if (texto) {
      return texto;
    }
  }

  return null;
}
